/**
 * Tax depreciation of fixed assets (ADR 0052, phase 3): a second schedule per asset kept beside the
 * book depreciation the ledger posts. Nothing here posts to the ledger; the schedule feeds the tax
 * computation, the practice year end and the gap between book and tax values.
 *
 * Every rule and preset in this file must be verified against current SARS guidance (Income Tax Act
 * sections 11(e), 11(o), 12B, 12C, 13quin and 8(4)(a); Interpretation Note 47) and IRS guidance
 * (Publication 946: MACRS, section 179, bonus depreciation; sections 1245 and 1250) before launch.
 * Limits that change every year (the section 179 ceiling and phase-out, the bonus percentage, the
 * small-item threshold) are not enforced: the business enters what it elects.
 *
 * Money is bigint (4 decimal places) and every fraction is an exact bigint rational. Amounts are
 * rounded half away from zero to whole cents, cumulatively, so a schedule's total equals the
 * depreciable basis exactly.
 */
import { CENT, type Money } from './money';

export type TaxRegime = 'za' | 'us';
export type TaxMethod = 'none' | 'straight_line' | 'percentages' | 'full' | 'macrs';
export type MacrsConvention = 'half_year' | 'mid_quarter' | 'mid_month';
export type MacrsMethod = '200db' | '150db' | 'sl';

export interface TaxAssetInput {
  method: TaxMethod;
  /** Tax cost: the basis before section 179 and bonus depreciation. */
  cost: Money;
  /** YYYY-MM-DD: the day the asset was brought into use (placed in service). */
  inServiceDate: string;
  disposedOn?: string | null;
  proceeds?: Money | null;
  /** straight_line: the write-off period in months. */
  lifeMonths?: number | null;
  /** straight_line: apportion the first and the disposal tax year by days in use. */
  prorate?: boolean;
  /** percentages: percent of cost per tax year, e.g. ['40', '20', '20', '20']. Never apportioned. */
  percentages?: string[] | null;
  /** macrs: the recovery period in years, '3' to '39' ('27.5' allowed). */
  macrsPeriod?: string | null;
  macrsConvention?: MacrsConvention | null;
  macrsMethod?: MacrsMethod | null;
  /** macrs: the section 179 expense elected, taken in the first year. */
  section179?: Money | null;
  /** macrs: bonus depreciation percent of the basis left after section 179, taken in the first year. */
  bonusPercent?: string | null;
  /** A migrated asset: tax years ending on or before this day are not listed. */
  openingDate?: string | null;
  /** Allowances already claimed in the tax years ending on or before openingDate. */
  claimedBefore?: Money | null;
}

export interface TaxYearRow {
  yearStart: string;
  yearEnd: string;
  openingValue: Money;
  allowance: Money;
  section179: Money;
  bonus: Money;
  closingValue: Money;
}

export interface TaxDisposal {
  /** The tax value (adjusted basis) left on the day of disposal. */
  taxValue: Money;
  proceeds: Money;
  /** South Africa, section 8(4)(a): allowances recovered, at most cost less tax value. */
  recoupment: Money;
  /** South Africa, section 11(o): tax value not recovered by the proceeds. */
  scrappingAllowance: Money;
  /** South Africa: proceeds above cost, for capital gains tax. */
  capitalGain: Money;
  /** United States: proceeds less adjusted basis (negative is a loss). */
  gain: Money;
  /** United States, section 1245: the part of the gain that is ordinary income. */
  recapture1245: Money;
}

export interface TaxSchedule {
  years: TaxYearRow[];
  disposal: TaxDisposal | null;
  /** Things the business should check, such as claimed allowances that differ from the schedule. */
  warnings: string[];
}

// ---------------------------------------------------------------------------------------------
// Exact rationals
// ---------------------------------------------------------------------------------------------

interface Rat {
  n: bigint;
  d: bigint;
}

const gcd = (a: bigint, b: bigint): bigint => {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x;
};
const rat = (n: bigint, d: bigint = 1n): Rat => {
  if (d === 0n) throw new RangeError('rational: zero denominator');
  const s = d < 0n ? -1n : 1n;
  const g = gcd(n, d) || 1n;
  return { n: (s * n) / g, d: (s * d) / g };
};
const ZERO = rat(0n);
const ONE = rat(1n);
const add = (a: Rat, b: Rat) => rat(a.n * b.d + b.n * a.d, a.d * b.d);
const sub = (a: Rat, b: Rat) => rat(a.n * b.d - b.n * a.d, a.d * b.d);
const mul = (a: Rat, b: Rat) => rat(a.n * b.n, a.d * b.d);
const div = (a: Rat, b: Rat) => rat(a.n * b.d, a.d * b.n);
const cmp = (a: Rat, b: Rat) => {
  const x = a.n * b.d - b.n * a.d;
  return x < 0n ? -1 : x > 0n ? 1 : 0;
};
const max = (a: Rat, b: Rat) => (cmp(a, b) >= 0 ? a : b);
const min = (a: Rat, b: Rat) => (cmp(a, b) <= 0 ? a : b);

/** A non-negative decimal string ('27.5', '40', '33.333') as an exact rational. */
function parseDecimal(input: string): Rat {
  const m = /^(\d+)(?:\.(\d+))?$/.exec(input.trim());
  if (!m) throw new RangeError(`invalid number "${input}"`);
  const frac = m[2] ?? '';
  return rat(BigInt((m[1] as string) + frac), 10n ** BigInt(frac.length));
}

/** amount × fraction, rounded half away from zero to whole cents. */
function times(amount: Money, f: Rat): Money {
  const num = amount * f.n;
  const den = f.d * CENT;
  const neg = num < 0n;
  const abs = neg ? -num : num;
  let cents = abs / den;
  if ((abs % den) * 2n >= den) cents += 1n;
  return (neg ? -cents : cents) * CENT;
}

const maxMoney = (a: Money, b: Money) => (a > b ? a : b);
const minMoney = (a: Money, b: Money) => (a < b ? a : b);

// ---------------------------------------------------------------------------------------------
// Days and tax years
// ---------------------------------------------------------------------------------------------

const DAY_MS = 86_400_000;
function dayNumber(date: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) throw new RangeError(`invalid date "${date}"`);
  const t = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const back = new Date(t).toISOString().slice(0, 10);
  if (back !== date) throw new RangeError(`invalid date "${date}"`);
  return t / DAY_MS;
}
const dateOf = (day: number) => new Date(day * DAY_MS).toISOString().slice(0, 10);
const lastDayOfMonth = (year: number, month: number) => dateOf(Date.UTC(year, month, 0) / DAY_MS); // month 1-12

/** The tax year holding a day, ending on the last day of yearEndMonth (1-12): Feb for SARS, Dec in the US. */
export function taxYearOf(date: string, yearEndMonth: number): { start: string; end: string } {
  if (!Number.isInteger(yearEndMonth) || yearEndMonth < 1 || yearEndMonth > 12) throw new RangeError('yearEndMonth is 1 to 12');
  dayNumber(date);
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  const endYear = month <= yearEndMonth ? year : year + 1;
  const end = lastDayOfMonth(endYear, yearEndMonth);
  const start = dateOf(dayNumber(lastDayOfMonth(endYear - 1, yearEndMonth)) + 1);
  return { start, end };
}

const nextYear = (y: { start: string; end: string }, yearEndMonth: number) => taxYearOf(dateOf(dayNumber(y.end) + 1), yearEndMonth);
const daysBetween = (from: string, to: string) => dayNumber(to) - dayNumber(from) + 1; // inclusive

/** The month (1-12) of a day within its tax year. */
function monthInTaxYear(date: string, yearStart: string): number {
  const sm = Number(yearStart.slice(5, 7));
  const m = Number(date.slice(5, 7));
  return ((m - sm + 12) % 12) + 1;
}

// ---------------------------------------------------------------------------------------------
// MACRS
// ---------------------------------------------------------------------------------------------

/** The GDS method for a recovery period: 200% declining balance to 10 years, 150% for 15 and 20, straight line for buildings. */
export function defaultMacrsMethod(period: string): MacrsMethod {
  const p = parseDecimal(period);
  if (cmp(p, rat(10n)) <= 0) return '200db';
  if (cmp(p, rat(20n)) <= 0) return '150db';
  return 'sl';
}

function macrsFirstYearFraction(convention: MacrsConvention, inService: string, yearStart: string): Rat {
  const m = monthInTaxYear(inService, yearStart);
  switch (convention) {
    case 'half_year':
      return rat(1n, 2n);
    case 'mid_quarter': {
      const q = Math.floor((m - 1) / 3) + 1; // 1-4
      return rat(BigInt(9 - 2 * q), 8n); // 7/8, 5/8, 3/8, 1/8
    }
    case 'mid_month':
      return rat(BigInt(2 * (12 - m) + 1), 24n); // (12 - m + 0.5) / 12
  }
}

function macrsDisposalFraction(convention: MacrsConvention, disposedOn: string, yearStart: string): Rat {
  const m = monthInTaxYear(disposedOn, yearStart);
  switch (convention) {
    case 'half_year':
      return rat(1n, 2n);
    case 'mid_quarter': {
      const q = Math.floor((m - 1) / 3) + 1;
      return rat(BigInt(2 * q - 1), 8n); // 1/8, 3/8, 5/8, 7/8
    }
    case 'mid_month':
      return rat(BigInt(2 * m - 1), 24n); // (m - 0.5) / 12
  }
}

/** The fraction of the basis allowed in each full tax year, in order, summing to exactly one. */
function macrsFractions(period: Rat, method: MacrsMethod, first: Rat): Rat[] {
  const factor = method === '200db' ? rat(2n) : method === '150db' ? rat(3n, 2n) : null;
  const out: Rat[] = [];
  let value = ONE;
  const y1 = factor ? mul(mul(value, div(factor, period)), first) : div(first, period);
  out.push(min(y1, value));
  value = sub(value, out[0] as Rat);
  let elapsed = first;
  for (let guard = 0; cmp(value, ZERO) > 0 && guard < 100; guard++) {
    const remainingLife = sub(period, elapsed);
    let a: Rat;
    if (cmp(remainingLife, ONE) <= 0) a = value;
    else {
      const sl = div(value, remainingLife);
      a = factor ? max(mul(value, div(factor, period)), sl) : sl;
      a = min(a, value);
    }
    out.push(a);
    value = sub(value, a);
    elapsed = add(elapsed, ONE);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// The schedule
// ---------------------------------------------------------------------------------------------

/**
 * The tax schedule of one asset: a row per tax year from the year it was brought into use until its
 * tax value is written off or it is disposed of (that year included), and the disposal's tax effect.
 */
export function taxSchedule(a: TaxAssetInput, yearEndMonth: number, regime: TaxRegime): TaxSchedule {
  if (a.cost < 0n) throw new RangeError('cost cannot be negative');
  const warnings: string[] = [];
  const inService = a.inServiceDate;
  const disposedOn = a.disposedOn ?? null;
  if (disposedOn && dayNumber(disposedOn) < dayNumber(inService)) throw new RangeError('disposal before the asset was brought into use');
  const disposalYear = disposedOn ? taxYearOf(disposedOn, yearEndMonth) : null;
  const opening = a.openingDate ?? null;
  const claimed = opening && a.claimedBefore != null ? a.claimedBefore : null;

  const rows: TaxYearRow[] = [];
  let allowed: Money = 0n; // allowances, 179 and bonus to date (all years, listed or not)

  const listed = (y: { end: string }) => !opening || dayNumber(y.end) > dayNumber(opening);
  const isDisposalYear = (y: { end: string }) => disposalYear !== null && disposalYear.end === y.end;
  const push = (y: { start: string; end: string }, openingValue: Money, allowance: Money, s179: Money, bonus: Money) => {
    if (listed(y)) rows.push({ yearStart: y.start, yearEnd: y.end, openingValue, allowance, section179: s179, bonus, closingValue: openingValue - allowance - s179 - bonus });
  };

  if (a.method === 'macrs') {
    const periodText = a.macrsPeriod;
    if (!periodText) throw new RangeError('macrs needs a recovery period');
    const period = parseDecimal(periodText);
    const method = a.macrsMethod ?? defaultMacrsMethod(periodText);
    const convention = a.macrsConvention ?? (method === 'sl' && cmp(period, rat(27n)) >= 0 ? 'mid_month' : 'half_year');
    const s179 = minMoney(maxMoney(a.section179 ?? 0n, 0n), a.cost);
    const bonusPct = a.bonusPercent ? parseDecimal(a.bonusPercent) : ZERO;
    if (cmp(bonusPct, rat(100n)) > 0) throw new RangeError('bonus is at most 100 percent');
    let y = taxYearOf(inService, yearEndMonth);
    const sameYearDisposal = disposalYear !== null && disposalYear.end === y.end;
    // Placed in service and disposed of in the same tax year: no MACRS deduction and no bonus.
    const bonus = sameYearDisposal ? 0n : times(a.cost - s179, div(bonusPct, rat(100n)));
    const basis = a.cost - s179 - bonus;
    const fractions = macrsFractions(period, method, macrsFirstYearFraction(convention, inService, y.start));
    let cum = ZERO;
    let cumAmount: Money = 0n;
    for (let k = 0; k < fractions.length; k++) {
      const openingValue = a.cost - allowed;
      let f = fractions[k] as Rat;
      const disposing = isDisposalYear(y);
      if (disposing) f = k === 0 ? ZERO : mul(f, macrsDisposalFraction(convention, disposedOn as string, y.start));
      cum = add(cum, f);
      const nextAmount = times(basis, cum);
      const allowance = nextAmount - cumAmount;
      cumAmount = nextAmount;
      const s = k === 0 ? s179 : 0n;
      const b = k === 0 ? bonus : 0n;
      push(y, openingValue, allowance, s, b);
      allowed += allowance + s + b;
      if (disposing) break;
      y = nextYear(y, yearEndMonth);
    }
    if (claimed !== null) {
      const before = rows.length > 0 ? a.cost - (rows[0] as TaxYearRow).openingValue : allowed;
      if (before !== claimed) warnings.push('The allowances already claimed differ from the MACRS schedule; the schedule follows MACRS.');
    }
  } else if (a.method !== 'none') {
    // Fraction of cost each tax year k would normally get (before capping).
    const annual = (k: number, y: { start: string; end: string }): Rat => {
      if (a.method === 'full') return k === 0 ? ONE : ZERO;
      if (a.method === 'percentages') {
        const p = (a.percentages ?? [])[k];
        return p === undefined ? ZERO : div(parseDecimal(p), rat(100n));
      }
      const life = a.lifeMonths;
      if (!life || life <= 0) throw new RangeError('straight_line needs a write-off period');
      let f = rat(12n, BigInt(life));
      if (a.prorate) {
        const from = k === 0 ? inService : y.start;
        const to = isDisposalYear(y) ? (disposedOn as string) : y.end;
        f = mul(f, rat(BigInt(daysBetween(from, to)), BigInt(daysBetween(y.start, y.end))));
      }
      return f;
    };
    let y = taxYearOf(inService, yearEndMonth);
    let cum = ZERO;
    let cumAmount: Money = 0n;
    let usingClaimed = false;
    for (let k = 0; k < 2000; k++) {
      const f = annual(k, y);
      let allowance: Money;
      if (claimed !== null && listed(y)) {
        if (!usingClaimed) {
          usingClaimed = true;
          allowed = claimed;
        }
        allowance = minMoney(times(a.cost, f), a.cost - allowed);
      } else {
        cum = min(add(cum, f), ONE);
        const nextAmount = times(a.cost, cum);
        allowance = nextAmount - cumAmount;
        cumAmount = nextAmount;
      }
      allowance = maxMoney(allowance, 0n);
      push(y, a.cost - allowed, allowance, 0n, 0n);
      allowed += allowance;
      if (isDisposalYear(y) || allowed >= a.cost) break;
      if (a.method === 'full') break;
      if (a.method === 'percentages' && k + 1 >= (a.percentages ?? []).length) break;
      y = nextYear(y, yearEndMonth);
    }
  }

  let disposal: TaxDisposal | null = null;
  if (disposedOn) {
    const proceeds = a.proceeds ?? 0n;
    const taxValue = a.cost - allowed;
    const none: TaxDisposal = { taxValue, proceeds, recoupment: 0n, scrappingAllowance: 0n, capitalGain: 0n, gain: 0n, recapture1245: 0n };
    if (regime === 'za') {
      const scrapping = a.method !== 'straight_line' || (a.lifeMonths ?? 0) <= 300;
      disposal = {
        ...none,
        recoupment: maxMoney(0n, minMoney(proceeds, a.cost) - taxValue),
        scrappingAllowance: scrapping ? maxMoney(0n, taxValue - proceeds) : 0n,
        capitalGain: maxMoney(0n, proceeds - a.cost),
      };
    } else {
      const gain = proceeds - taxValue;
      const realProperty = a.method === 'macrs' && a.macrsPeriod != null && cmp(parseDecimal(a.macrsPeriod), rat(27n)) >= 0;
      disposal = { ...none, gain, recapture1245: realProperty ? 0n : maxMoney(0n, minMoney(gain, a.cost - taxValue)) };
    }
  }
  return { years: rows, disposal, warnings };
}

// ---------------------------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------------------------

export interface TaxPreset {
  key: string;
  regime: TaxRegime;
  label: string;
  method: TaxMethod;
  lifeMonths?: number;
  prorate?: boolean;
  percentages?: string[];
  macrsPeriod?: string;
  macrsConvention?: MacrsConvention;
  macrsMethod?: MacrsMethod;
  reference: string;
}

const za = (key: string, label: string, years: number, reference = 'Section 11(e); Interpretation Note 47, Annexure A'): TaxPreset => ({
  key: `za_${key}`, regime: 'za', label, method: 'straight_line', lifeMonths: years * 12, prorate: true, reference,
});
const us = (period: string, label: string, method?: MacrsMethod, convention: MacrsConvention = 'half_year'): TaxPreset => ({
  key: `us_macrs_${period.replace('.', '_')}`, regime: 'us', label, method: 'macrs', macrsPeriod: period,
  macrsMethod: method ?? defaultMacrsMethod(period), macrsConvention: convention, reference: 'MACRS GDS, IRS Publication 946',
});

/** Starting points for an asset type's tax treatment. Verify each against current SARS and IRS guidance before launch. */
export const TAX_PRESETS: readonly TaxPreset[] = [
  za('personal_computers', 'Personal computers (3 years)', 3),
  za('computer_software', 'Computer software, mainframe, purchased (3 years)', 3),
  za('office_equipment_electronic', 'Office equipment, electronic (3 years)', 3),
  za('office_equipment_mechanical', 'Office equipment, mechanical (5 years)', 5),
  za('office_furniture', 'Furniture and fittings (6 years)', 6),
  za('motor_vehicles', 'Motor vehicles, passenger (5 years)', 5),
  za('delivery_vehicles', 'Delivery vehicles (4 years)', 4),
  za('cellphones', 'Cellular telephones (2 years)', 2),
  za('air_conditioners', 'Air conditioners (6 years)', 6),
  za('tools', 'Tools (6 years)', 6),
  { key: 'za_small_items', regime: 'za', label: 'Small items under R7 000, written off in full', method: 'full', reference: 'Interpretation Note 47, small items' },
  { key: 'za_s12c_new', regime: 'za', label: 'Manufacturing plant, new and unused (40/20/20/20)', method: 'percentages', percentages: ['40', '20', '20', '20'], reference: 'Section 12C(1)' },
  { key: 'za_s12c_used', regime: 'za', label: 'Manufacturing plant, used (20% a year)', method: 'percentages', percentages: ['20', '20', '20', '20', '20'], reference: 'Section 12C(1)' },
  { key: 'za_s12b_renewable', regime: 'za', label: 'Renewable energy (50/30/20)', method: 'percentages', percentages: ['50', '30', '20'], reference: 'Section 12B' },
  { key: 'za_s13quin_buildings', regime: 'za', label: 'Commercial buildings (5% a year)', method: 'straight_line', lifeMonths: 240, prorate: false, reference: 'Section 13quin' },
  us('3', '3-year property (tractor units, special tools)'),
  us('5', '5-year property (computers, cars, light trucks, office machinery)'),
  us('7', '7-year property (office furniture and fixtures)'),
  us('10', '10-year property (vessels, single-purpose agricultural structures)'),
  us('15', '15-year property (land improvements, qualified improvement property)', '150db'),
  us('20', '20-year property (farm buildings, municipal sewers)', '150db'),
  us('27.5', 'Residential rental property (27.5 years)', 'sl', 'mid_month'),
  us('39', 'Nonresidential real property (39 years)', 'sl', 'mid_month'),
];

export function taxPresetFor(key: string): TaxPreset | undefined {
  return TAX_PRESETS.find((p) => p.key === key);
}
